import { useMemo } from "react";

import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { TRANSPORT_STAGES, TransportStage } from "./transportStages";

/**
 * Mapping `TransportStage -> { parents, subFlag }` mirroring the backend
 * {@code App\Service\Transport\TransportStageGate}. Kept in sync by hand —
 * the source of truth is the backend.
 *
 * - `parents`: ALL listed flags must be enabled for the stage to be available.
 * - `subFlag`: dedicated sub-flag for the stage (null = no sub-flag, always
 *   enabled when the master + parents are on).
 */
const STAGE_DEPENDENCY_MAP: Record<
    TransportStage,
    {
        parents: ReadonlyArray<string>;
        subFlag: string | null;
    }
> = {
    product_to_storage: {
        parents: [],
        subFlag: "FS-transport-stage-product-to-storage",
    },
    ingredient_to_processing_site: {
        parents: ["FS-entity-ingredients"],
        subFlag: "FS-transport-stage-ingredient-to-processing-site",
    },
    packaging_to_processing_site: {
        parents: ["FS-entity-packagings"],
        subFlag: "FS-transport-stage-packaging-to-processing-site",
    },
    retail_to_consumer: {
        parents: ["FS-product-consumption"],
        subFlag: "FS-transport-stage-retail-to-consumer",
    },
    storage_to_retail: {
        parents: ["FS-product-distribution"],
        subFlag: "FS-transport-stage-storage-to-retail",
    },
    custom: {
        parents: [],
        subFlag: null,
    },
};

/**
 * Returns the set of transport stages currently enabled for the active tenant.
 * Mirrors backend {@code TransportStageGate} so the UI hides UI rows for
 * disabled stages without an extra round trip — the API still revalidates
 * writes via {@code TransportStageEnabledConstraint}.
 */
export const useEnabledTransportStages = (): {
    enabled: ReadonlySet<TransportStage>;
    isStageEnabled: (stage: TransportStage) => boolean;
    parentsForStage: (stage: TransportStage) => ReadonlyArray<string>;
    subFlagForStage: (stage: TransportStage) => string | null;
} => {
    const { isFeatureActivated } = useApplicationParameters();

    const enabled = useMemo<Set<TransportStage>>(() => {
        const set = new Set<TransportStage>();
        if (!isFeatureActivated("FS-product-transport")) {
            return set;
        }

        for (const stage of TRANSPORT_STAGES) {
            const { parents, subFlag } = STAGE_DEPENDENCY_MAP[stage];
            const parentsOk = parents.every((parent) => isFeatureActivated(parent));
            if (!parentsOk) continue;

            if (subFlag === null || isFeatureActivated(subFlag)) {
                set.add(stage);
            }
        }

        return set;
    }, [isFeatureActivated]);

    return {
        enabled,
        isStageEnabled: (stage) => enabled.has(stage),
        parentsForStage: (stage) => STAGE_DEPENDENCY_MAP[stage].parents,
        subFlagForStage: (stage) => STAGE_DEPENDENCY_MAP[stage].subFlag,
    };
};
